import {
  LayoutDashboard,
  School,
  Calendar,
  FileText,
  Trophy,
  Award,
  Heart,
  Users,
  Shield,
  type LucideIcon,
} from "lucide-react";

/** Permission names — must match backend seed data in tenant-permissions. */
export const ADMIN_PERMISSIONS = {
  DASHBOARD: "karuna.admin.dashboard",
  SCHOOLS: "karuna.admin.schools",
  ACTIVITY: "karuna.admin.activity",
  SUBMISSIONS: "karuna.admin.submissions",
  SUBMISSION_REPORTS: "karuna.admin.submission_reports",
  SCHOOL_RANKINGS: "karuna.admin.school_rankings",
  CERTIFICATES: "karuna.admin.certificates",
  DONATIONS: "karuna.admin.donations",
  CONTACT_US: "karuna.admin.contact_us",
  STUDENTS: "karuna.admin.students",
  SETTINGS: "karuna.admin.settings",
  USERS: "karuna.admin.users",
} as const;

export type AdminPermissionKey =
  (typeof ADMIN_PERMISSIONS)[keyof typeof ADMIN_PERMISSIONS];

export interface AdminMenuItem {
  title: string;
  url: string;
  icon: LucideIcon;
  permission: AdminPermissionKey;
}

export const ADMIN_MENU_ITEMS: AdminMenuItem[] = [
  {
    title: "Dashboard",
    url: "/admin/dashboard",
    icon: LayoutDashboard,
    permission: ADMIN_PERMISSIONS.DASHBOARD,
  },
  {
    title: "Schools",
    url: "/admin/schools",
    icon: School,
    permission: ADMIN_PERMISSIONS.SCHOOLS,
  },
  {
    title: "Activity",
    url: "/admin/activity",
    icon: Calendar,
    permission: ADMIN_PERMISSIONS.ACTIVITY,
  },
  {
    title: "Submissions",
    url: "/admin/submissions",
    icon: FileText,
    permission: ADMIN_PERMISSIONS.SUBMISSIONS,
  },
  {
    title: "Submission Reports",
    url: "/admin/submission-reports",
    icon: Trophy,
    permission: ADMIN_PERMISSIONS.SUBMISSION_REPORTS,
  },
  {
    title: "School Rankings",
    url: "/admin/school-rankings",
    icon: FileText,
    permission: ADMIN_PERMISSIONS.SCHOOL_RANKINGS,
  },
  {
    title: "Certificates",
    url: "/admin/certificates",
    icon: Award,
    permission: ADMIN_PERMISSIONS.CERTIFICATES,
  },
  {
    title: "Donations",
    url: "/admin/donations",
    icon: Heart,
    permission: ADMIN_PERMISSIONS.DONATIONS,
  },
  {
    title: "Contact Us",
    url: "/admin/contact-us",
    icon: Users,
    permission: ADMIN_PERMISSIONS.CONTACT_US,
  },
  {
    title: "Super Admins",
    url: "/admin/users",
    icon: Shield,
    permission: ADMIN_PERMISSIONS.USERS,
  },
];

/** Route path (under /admin) → permission key */
export const ADMIN_ROUTE_PERMISSIONS: Record<string, AdminPermissionKey> = {
  dashboard: ADMIN_PERMISSIONS.DASHBOARD,
  schools: ADMIN_PERMISSIONS.SCHOOLS,
  "school-approval": ADMIN_PERMISSIONS.SCHOOLS,
  activity: ADMIN_PERMISSIONS.ACTIVITY,
  submissions: ADMIN_PERMISSIONS.SUBMISSIONS,
  "submission-reports": ADMIN_PERMISSIONS.SUBMISSION_REPORTS,
  "school-rankings": ADMIN_PERMISSIONS.SCHOOL_RANKINGS,
  certificates: ADMIN_PERMISSIONS.CERTIFICATES,
  donations: ADMIN_PERMISSIONS.DONATIONS,
  "contact-us": ADMIN_PERMISSIONS.CONTACT_US,
  students: ADMIN_PERMISSIONS.STUDENTS,
  settings: ADMIN_PERMISSIONS.SETTINGS,
  users: ADMIN_PERMISSIONS.USERS,
};

export const ADMIN_MODULE_LABELS: Record<AdminPermissionKey, string> = {
  [ADMIN_PERMISSIONS.DASHBOARD]: "Dashboard",
  [ADMIN_PERMISSIONS.SCHOOLS]: "Schools",
  [ADMIN_PERMISSIONS.ACTIVITY]: "Activity",
  [ADMIN_PERMISSIONS.SUBMISSIONS]: "Submissions",
  [ADMIN_PERMISSIONS.SUBMISSION_REPORTS]: "Submission Reports",
  [ADMIN_PERMISSIONS.SCHOOL_RANKINGS]: "School Rankings",
  [ADMIN_PERMISSIONS.CERTIFICATES]: "Certificates",
  [ADMIN_PERMISSIONS.DONATIONS]: "Donations",
  [ADMIN_PERMISSIONS.CONTACT_US]: "Contact Us",
  [ADMIN_PERMISSIONS.STUDENTS]: "Students",
  [ADMIN_PERMISSIONS.SETTINGS]: "Settings",
  [ADMIN_PERMISSIONS.USERS]: "Super Admins",
};
